import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    resetLearningElementProgress,
    resetLearningSectionsProgress,
} from '@routes/sidebar/learning/___pages___/learning-overview/___shared___/helpers.ts';
import type {
    GeneralFeedbackRequest,
    LearningOverviewResponse,
    PathwayElementRequest,
    PathwayRateFeedbackRequest,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/learningOverview.api.ts';
import {
    getApiLearningOverview,
    postApiResetPathway,
    putApiCompletedPathwayElement,
    putApiGeneralFeedback,
    putApiPathwaysRateFeedback,
    putApiVisitedPathwayElement,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/learningOverview.api.ts';
import type { GeneralError } from '@types';
import { extractError } from '@utils/slice.ts';

interface LearningOverviewSlice {
    data: LearningOverviewResponse | null;
    error: Record<string, any> | null;
    overviewNavKey: string;
    isShareContentFeedbackModalOpened: boolean;
    isShareRateFeedbackModalOpened: boolean;
    learningElementId: number | null;
    currentVideoTime: number | null;
    showPathwayCompletedOutro: boolean;
    isAdaptiveActive: boolean;
    isAdaptiveContext: boolean;
    adaptiveQuizId: number | null;
    isRestartPathwayModalOpened: boolean;

    isGetLearningOverviewPending: boolean;
    isResetPathwayPending: boolean;
    isShareFeedbackPending: boolean;
    isShareRateFeedbackPending: boolean;
    isSentCompletedPathwayElementPending: boolean;
}

const initialState: LearningOverviewSlice = {
    data: null,
    error: null,
    overviewNavKey: '',
    isShareContentFeedbackModalOpened: false,
    isShareRateFeedbackModalOpened: false,
    learningElementId: null,
    currentVideoTime: null,
    showPathwayCompletedOutro: false,
    isAdaptiveActive: false, // quiz in progress
    isAdaptiveContext: false, // display quiz related UI elements (e.g. start btn)
    adaptiveQuizId: null,
    isRestartPathwayModalOpened: false,

    isGetLearningOverviewPending: true,
    isResetPathwayPending: false,
    isShareFeedbackPending: false,
    isShareRateFeedbackPending: false,
    isSentCompletedPathwayElementPending: false,
};

export const getLearningOverview = createAsyncThunk<LearningOverviewResponse, number, { rejectValue: any }>(
    'overview/getLearningOverview',
    (id, { rejectWithValue }) =>
        getApiLearningOverview(id)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const shareFeedback = createAsyncThunk<
    void,
    { payload: GeneralFeedbackRequest; pathwayId: string },
    { rejectValue: GeneralError }
>('overview/shareFeedbackOnContent', ({ payload, pathwayId }, { rejectWithValue }) =>
    putApiGeneralFeedback(payload, pathwayId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const shareRateFeedback = createAsyncThunk<
    void,
    { payload: PathwayRateFeedbackRequest; pathwayId: string },
    { rejectValue: GeneralError }
>('overview/shareRateFeedback', ({ payload, pathwayId }, { rejectWithValue }) =>
    putApiPathwaysRateFeedback(payload, pathwayId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const sentCompletedPathwayElement = createAsyncThunk<
    void,
    { payload: PathwayElementRequest; pathwayId: string },
    { rejectValue: any }
>('overview/sentApiCompletedPathwayElement', ({ payload, pathwayId }, { rejectWithValue }) =>
    putApiCompletedPathwayElement(payload, pathwayId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const sentVisitedPathwayElement = createAsyncThunk<
    void,
    { elementId: number; pathwayId: string },
    { rejectValue: any }
>('overview/sentApiVisitedPathwayElement', ({ elementId, pathwayId }, { rejectWithValue }) =>
    putApiVisitedPathwayElement(pathwayId, elementId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const resetPathway = createAsyncThunk<LearningOverviewResponse, string, { rejectValue: any }>(
    'overview/resetPathway',
    (pathwayId, { rejectWithValue }) =>
        postApiResetPathway(pathwayId)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

const learningOverviewSlice = createSlice({
    name: 'overview',
    initialState,
    reducers: {
        // get overviewNavKey from navigation pathway menu to know which part from learning pathway we need to render
        setOverviewNavKey(state, action: PayloadAction<string>) {
            state.overviewNavKey = action.payload;
        },
        setIsShareContentFeedbackModalOpened(state, action: PayloadAction<boolean>) {
            state.isShareContentFeedbackModalOpened = action.payload;
        },
        setIsRateFeedbackModalOpened(state, action: PayloadAction<boolean>) {
            state.isShareRateFeedbackModalOpened = action.payload;
        },
        setLearningElementId(state, action: PayloadAction<number | null>) {
            state.learningElementId = action.payload;
        },
        setCurrentVideoTime(state, action: PayloadAction<number | null>) {
            state.currentVideoTime = action.payload;
        },
        setShowPathwayCompletedOutro(state, action: PayloadAction<boolean>) {
            state.showPathwayCompletedOutro = action.payload;
        },
        setIsAdaptiveActive(state, action: PayloadAction<boolean>) {
            state.isAdaptiveActive = action.payload;
        },
        setIsAdaptiveContext(state, action: PayloadAction<boolean>) {
            state.isAdaptiveContext = action.payload;
        },
        setAdaptiveContextFromOverview(
            state,
            action: PayloadAction<
                Pick<LearningOverviewResponse, 'isAdaptiveEnabled' | 'isGlobalAdaptiveEnabled' | 'adaptiveQuiz'>
            >,
        ) {
            const overview = action.payload;
            const isAdaptiveContextAvailable = overview.isAdaptiveEnabled && overview.isGlobalAdaptiveEnabled;

            if (isAdaptiveContextAvailable) {
                state.isAdaptiveContext = true;
                state.adaptiveQuizId = overview.adaptiveQuiz?.id ?? null;
                return;
            }

            state.isAdaptiveContext = false;
        },
        setIsRestartPathwayModalOpened(state, action: PayloadAction<boolean>) {
            state.isRestartPathwayModalOpened = action.payload;
        },
        resetPathwayElementsProgress(state) {
            if (!state.data) return;

            state.data.sections = resetLearningSectionsProgress(state.data.sections);

            if (state.data.finalQuiz) {
                state.data.finalQuiz = resetLearningElementProgress(state.data.finalQuiz);
            }

            if (state.data.adaptiveQuiz) {
                state.data.adaptiveQuiz = resetLearningElementProgress(state.data.adaptiveQuiz);
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getLearningOverview.pending, (state) => {
            state.error = null;
            state.isGetLearningOverviewPending = true;
        });
        builder.addCase(getLearningOverview.fulfilled, (state, action) => {
            state.data = action.payload;
            state.isGetLearningOverviewPending = false;
            learningOverviewSlice.caseReducers.setAdaptiveContextFromOverview(state, action);
        });
        builder.addCase(getLearningOverview.rejected, (state, action) => {
            state.error = extractError(action);
            state.isGetLearningOverviewPending = false;
        });

        builder.addCase(shareFeedback.pending, (state) => {
            state.isShareFeedbackPending = true;
        });
        builder.addCase(shareFeedback.fulfilled, (state) => {
            state.isShareFeedbackPending = false;
        });
        builder.addCase(shareFeedback.rejected, (state, action) => {
            state.isShareFeedbackPending = false;
            state.error = extractError(action);
        });

        builder.addCase(shareRateFeedback.pending, (state) => {
            state.isShareRateFeedbackPending = true;
        });
        builder.addCase(shareRateFeedback.fulfilled, (state) => {
            state.isShareRateFeedbackPending = false;
        });
        builder.addCase(shareRateFeedback.rejected, (state, action) => {
            state.isShareRateFeedbackPending = false;
            state.error = extractError(action);
        });

        builder.addCase(sentCompletedPathwayElement.pending, (state) => {
            state.isSentCompletedPathwayElementPending = true;
        });
        builder.addCase(sentCompletedPathwayElement.fulfilled, (state) => {
            state.isSentCompletedPathwayElementPending = false;
        });
        builder.addCase(sentCompletedPathwayElement.rejected, (state, action) => {
            state.isSentCompletedPathwayElementPending = false;
            state.error = extractError(action);
        });

        builder.addCase(sentVisitedPathwayElement.rejected, (state, action) => {
            state.error = extractError(action);
        });

        builder.addCase(resetPathway.pending, (state) => {
            state.error = null;
            state.isResetPathwayPending = true;
        });
        builder.addCase(resetPathway.fulfilled, (state, action) => {
            state.data = action.payload;
            state.isResetPathwayPending = false;
            learningOverviewSlice.caseReducers.setAdaptiveContextFromOverview(state, action);
        });
        builder.addCase(resetPathway.rejected, (state, action) => {
            state.error = extractError(action);
            state.isResetPathwayPending = false;
        });
    },
});

export const {
    setOverviewNavKey,
    setIsShareContentFeedbackModalOpened,
    setIsRateFeedbackModalOpened,
    setLearningElementId,
    setCurrentVideoTime,
    setShowPathwayCompletedOutro,
    setIsAdaptiveActive,
    setIsAdaptiveContext,
    setAdaptiveContextFromOverview,
    setIsRestartPathwayModalOpened,
    resetPathwayElementsProgress,
} = learningOverviewSlice.actions;
export default learningOverviewSlice.reducer;
